import { Injectable } from '@angular/core';
import { FareOptimisationType } from "@afidium/ts-facade/model/fareOptimisationType";
import {
    PriceInBookingType,
    PriceItemType,
    CurrencyUnitTotalType,
    PriceSignatureType,
    BookingType,
    TravellerXRefType
} from "@afidium/ts-facade";
import { FareOptimisationOfferType } from "@afidium/ts-facade/model/fareOptimisationOfferType";
import { Observable } from "rxjs";
import { IFormattedFareOptimisation, IFormattedFareOptimisations } from "libraries/interfaces/fare-optimisation/IFareOptimisation";
import { map } from "rxjs/operators";
import { Yii } from "libraries/interfaces/Yii";
import { PricesTableService } from "libraries/component/booking-information/components/booking-price-card/services/prices-table.service";
import { SectorInBookingType } from "@afidium/ts-facade/model/sectorInBookingType";
import { PriceDetailInBookingType } from "@afidium/ts-facade/model/priceDetailInBookingType";
import { TotalPerTravellerType } from "@afidium/ts-facade/model/totalPerTravellerType";
import { PriceEnvelopeInBookingType } from "@afidium/ts-facade/model/priceEnvelopeInBookingType";
import { AirBookingResourceService } from "libraries/service/resources/air-booking/air-booking-resource.service";

@Injectable()
export class FareOptimisationService {

    constructor(
        private readonly airBookingResourceService: AirBookingResourceService,
        private readonly pricesTablesService: PricesTableService,
    ) {
    }

    public formatFareOptimisations(fareOptimisation: FareOptimisationType, prices: PriceInBookingType[], booking?: BookingType): IFormattedFareOptimisations {
        const formattedFareOptimisations: IFormattedFareOptimisation[] = [];
        fareOptimisation.fareOptimisationOfferToBeProcessed.fareOptimisationOfferArray
            .forEach((fareOptimisationOffer: FareOptimisationOfferType): void => {
                const sectorsFound: SectorInBookingType[] = this.pricesTablesService.getSectorsBySignature(fareOptimisationOffer.price, booking, 'cost');
                formattedFareOptimisations.push({
                    offer: fareOptimisationOffer,
                    prices: [],
                    total: {},
                    offerDbId: fareOptimisationOffer.offer?.dbId?.toString(),
                    price: fareOptimisationOffer.price,
                    sectors: sectorsFound.filter((element: SectorInBookingType) => Boolean(element)),
                });
            });
        if (prices?.length) {
            this.getOptimisationLinkedPrices(formattedFareOptimisations, prices);
        }
        this.getOptimisationsSaving(formattedFareOptimisations);

        return {
            fareOptimisations: formattedFareOptimisations,
            potentialSaving: this.getSaving(fareOptimisation)
        };
    }

    public returnOptimisation(bookingSyscode: string, airOfferId: string): Observable<any> {
        return this.airBookingResourceService.getOptimisation(bookingSyscode, airOfferId)
            .pipe(
                map((value: any): any | Error => {
                    if (value) {
                        return value;
                    }
                    throw new Error(Yii.t('wui-sasha-js', 'Optimisation non trouvée.'));
                })
            );
    }

    public filterPaxOnTarificationPrice(optimisationPrice: PriceEnvelopeInBookingType, travellerSignature: string): void {
        this.changeTotalPriceThanksToTravellerPrice(travellerSignature, optimisationPrice.total?.totalArray);
        this.changeTotalPriceThanksToTravellerPrice(travellerSignature, optimisationPrice.total?.baseArray);
        this.changeTotalPriceThanksToTravellerPrice(travellerSignature, optimisationPrice.total?.taxArray);
        this.changeTotalPriceThanksToTravellerPrice(travellerSignature, optimisationPrice.total?.surchargeArray);

        (optimisationPrice.detailArray || []).forEach((detail: PriceDetailInBookingType): void => {
            if (detail.travellerArray) {
                detail.travellerArray = detail.travellerArray.filter((traveller: TravellerXRefType): boolean => traveller.signature === travellerSignature) ?? [];
            }
        });
    }

    private getOptimisationLinkedPrices(formattedFareOptimisations: IFormattedFareOptimisation[], prices: PriceInBookingType[]): void {
        formattedFareOptimisations
            .forEach((formattedOptimisationOffer: IFormattedFareOptimisation): void => {
                formattedOptimisationOffer.offer.booking.priceArray.forEach((fareOptimisationOfferBookingPrice: PriceSignatureType): void => {
                    const linkedPrices: PriceInBookingType[] = prices.filter((price: PriceInBookingType): boolean => {
                        return price.cost.signature === fareOptimisationOfferBookingPrice.signature;
                    });
                    linkedPrices.forEach((price: PriceInBookingType): void => {
                        formattedOptimisationOffer.prices.push(price.cost.index?.toString() ?? price.cost.xId);
                    });
                });
            });
    }

    private getOptimisationsSaving(formattedFareOptimisations: IFormattedFareOptimisation[]): void {
        formattedFareOptimisations
            .forEach((fareOptimisationOffer: IFormattedFareOptimisation): void => {
                for (const saving of fareOptimisationOffer.offer.savingArray) {
                    const properTotal: PriceItemType = saving.cost.totalArray.find((total: PriceItemType): boolean => !total.xReason);
                    if (properTotal && properTotal.currencyArray?.length) {
                        fareOptimisationOffer.total = properTotal.currencyArray[0];
                        break;
                    }
                }
            });
    }

    private getSaving(fareOptimisations: FareOptimisationType): CurrencyUnitTotalType {
        return fareOptimisations.savingToBeProcessed.cost.totalArray?.[0].currencyArray[0];
    }

    private changeTotalPriceThanksToTravellerPrice(travellerSignature: string, priceArray?: PriceItemType[]): void {
        (priceArray || []).forEach((element: PriceItemType): void => {
            const totalForTraveller: TotalPerTravellerType[] = element.totalPerTravellerArray.filter((total: TotalPerTravellerType): boolean => total.traveller.signature === travellerSignature);
            if (totalForTraveller?.length) {
                for (const totalPerTravellerType of totalForTraveller[0].total.currencyArray) {
                    if (!totalPerTravellerType.value && totalPerTravellerType.unitValue) {
                        totalPerTravellerType.value = totalPerTravellerType.unitValue;
                    }
                    if (!totalPerTravellerType.amount && totalPerTravellerType.unitAmount) {
                        totalPerTravellerType.amount = totalPerTravellerType.unitAmount;
                    }
                }
                element.currencyArray = totalForTraveller[0].total.currencyArray;
                element.totalPerTravellerArray = totalForTraveller;
            }
        });
    }
}
